/**
 * Shared enumerations and constant identifiers.
 */

// --- Scene Keys ---
export const SCENE_BOOT = 'BootScene';
export const SCENE_TITLE = 'TitleScene';
export const SCENE_GAME = 'GameScene';
export const SCENE_HUD = 'HudScene';
export const SCENE_GAME_OVER = 'GameOverScene';
export const SCENE_HELP = 'HelpScene';
export const SCENE_SHOP = 'ShopScene';
export const SCENE_CONFIG = 'ConfigScene';
export const SCENE_MANUSCRIPTS = 'ManuscriptsScene';
export const SCENE_ACHIEVEMENTS = 'AchievementsScene';
export const SCENE_RECORDS = 'RecordsScene';

// --- Game Modes ---
export const GameMode = Object.freeze({
  CLASSIC: 'classic',
  EXPLORER: 'explorer',
});

// --- Level Events ---
export const LevelEvent = Object.freeze({
  NONE: 'none',
  VORATHS_HUNGER: 'voraths-hunger',         // Food lifetime halved
  THREIAS_LIGHT: 'threias-light',           // Fog reveal radius increased (+2)
  SILENT_MAZE: 'silent-maze',               // No bats, no narrator
  GOLD_RUSH: 'gold-rush',                   // 3x coin spawns, no food
  LABYRINTH_REMEMBERS: 'labyrinth-remembers', // Visited fog re-darkens over time
});

// Level event display names (shown in level announcement)
export const LEVEL_EVENT_NAMES = Object.freeze({
  [LevelEvent.VORATHS_HUNGER]: "Vorath's Hunger",
  [LevelEvent.THREIAS_LIGHT]: "Threia's Light",
  [LevelEvent.SILENT_MAZE]: 'The Silent Maze',
  [LevelEvent.GOLD_RUSH]: 'Gold Rush',
  [LevelEvent.LABYRINTH_REMEMBERS]: 'The Labyrinth Remembers',
});

// Level event descriptions (for help screen and HUD tooltip)
export const LEVEL_EVENT_DESCRIPTIONS = Object.freeze({
  [LevelEvent.VORATHS_HUNGER]: 'Food disappears faster — Vorath devours sustenance',
  [LevelEvent.THREIAS_LIGHT]: 'Threia illuminates the path — expanded vision',
  [LevelEvent.SILENT_MAZE]: 'An eerie silence — no bats, no voices',
  [LevelEvent.GOLD_RUSH]: 'Gold floods the corridors — coins everywhere, but no food',
  [LevelEvent.LABYRINTH_REMEMBERS]: 'The maze forgets your steps — fog returns to visited areas',
});

// Selectable level events array (excludes NONE)
export const LEVEL_EVENTS_POOL = Object.freeze([
  LevelEvent.VORATHS_HUNGER,
  LevelEvent.THREIAS_LIGHT,
  LevelEvent.SILENT_MAZE,
  LevelEvent.GOLD_RUSH,
  LevelEvent.LABYRINTH_REMEMBERS,
]);

// --- Texture Keys (generated procedurally in BootScene) ---
export const TEX_PLAYER = 'player';
export const TEX_WALL = 'wall';
export const TEX_WALL_BRICK = 'wall-brick';
export const TEX_WALL_STONE = 'wall-stone';
export const TEX_WALL_WOOD = 'wall-wood';
export const TEX_WALL_METAL = 'wall-metal';
export const TEX_WALL_LATTICE = 'wall-lattice';
export const TEX_WALL_RUINS = 'wall-ruins';
export const TEX_WALL_MARBLE = 'wall-marble';
export const TEX_WALL_ADOBE = 'wall-adobe';
export const TEX_FOOD_FRUIT = 'food-fruit';
export const TEX_FOOD_CHICKEN = 'food-chicken';
export const TEX_FOOD_TUNA = 'food-tuna';
export const TEX_COIN = 'coin';
export const TEX_LIFE_ICON = 'life-icon';
export const TEX_EXIT = 'exit';
export const TEX_DUST = 'dust';
export const TEX_GLOW = 'glow';
export const TEX_WATER = 'water';
export const TEX_LAVA = 'lava';
export const TEX_SMOKE = 'smoke';
export const TEX_LAVA_RIM = 'lava-rim';
export const TEX_POISON = 'poison';
export const TEX_POISON_RIM = 'poison-rim';
export const TEX_SPIKE = 'spike';
export const TEX_SPIKE_RIM = 'spike-rim';
export const TEX_BLOOD = 'blood';
export const TEX_KEY_METALLIC = 'key-metallic';
export const TEX_KEY_COPPER = 'key-copper';
export const TEX_KEY_GOLD = 'key-gold';
export const TEX_CHEST_METALLIC = 'chest-metallic';
export const TEX_CHEST_COPPER = 'chest-copper';
export const TEX_CHEST_GOLD = 'chest-gold';
export const TEX_LOOT_HEART = 'loot-heart';
export const TEX_LOOT_STAR = 'loot-star';
export const TEX_LOOT_AGMUTH = 'loot-agmuth';
export const TEX_LOOT_COIN_BAG = 'loot-coin-bag';
export const TEX_COMPASS = 'compass';
export const TEX_COMPASS_NEEDLE = 'compass-needle';
export const TEX_THREIA_TOTEM = 'threia-totem';
export const TEX_THREIA_GLOW = 'threia-glow';

// Bat textures (two wing animation frames)
export const TEX_BAT_WINGS_UP = 'bat-wings-up';
export const TEX_BAT_WINGS_DOWN = 'bat-wings-down';

// Shop item textures
export const TEX_SHOP_THREIAS_GUIDE = 'shop-threias-guide';

// Manuscript floor pickup texture
export const TEX_MANUSCRIPT = 'manuscript';
export const TEX_SHOP_HEART_POTION = 'shop-heart-potion';
export const TEX_SHOP_TOTEM_VORACIOUS = 'shop-totem-voracious';
export const TEX_SHOP_AGMUTH_PENDANT = 'shop-agmuth-pendant';
export const TEX_SHOP_LAVA_RING = 'shop-lava-ring';
export const TEX_SHOP_POISON_RING = 'shop-poison-ring';
export const TEX_SHOP_WATER_RING = 'shop-water-ring';
export const TEX_SHOP_KEY_PENDANT = 'shop-key-pendant';
export const TEX_SHOP_HEART_PENDANT = 'shop-heart-pendant';
export const TEX_SHOP_ELEMENTAL_RING = 'shop-elemental-ring';
export const TEX_SHOP_TEAR_OF_THREIA = 'shop-tear-of-threia';

// Hammer of Threia textures
export const TEX_HAMMER_OF_THREIA = 'hammer-of-threia';
export const TEX_WALL_DEBRIS = 'wall-debris';
export const TEX_SHOP_HAMMER_OF_THREIA = 'shop-hammer-of-threia';

// Vorath's Treasure Room textures
export const TEX_GOLDEN_EXIT = 'golden-exit';
export const TEX_GOLDEN_EXIT_SPENT = 'golden-exit-spent';

// Secret Passage textures
export const TEX_SECRET_WALL = 'secret-wall';

// Shadow Walker textures
export const TEX_SHADOW_WALKER = 'shadow-walker';

// Bat Nest textures
export const TEX_BAT_NEST = 'bat-nest';
export const TEX_BAT_NEST_DESTROYED = 'bat-nest-destroyed';
export const TEX_WALL_CRUMBLED = 'wall-crumbled';

// Final Battle textures
export const TEX_CELESTIAL_EXIT = 'celestial-exit';
export const TEX_VORATH_MASS = 'vorath-mass';
export const TEX_ALTAR_THREIA = 'altar-threia';
export const TEX_DARK_SPHERE = 'dark-sphere';
export const TEX_BEAM_PARTICLE = 'beam-particle';

// --- Key Tiers ---
export const KeyTier = Object.freeze({
  METALLIC: 'metallic',
  COPPER: 'copper',
  GOLD: 'gold',
});

// Tier ordering (lowest to highest) for key consumption priority
export const KEY_TIER_ORDER = Object.freeze([KeyTier.METALLIC, KeyTier.COPPER, KeyTier.GOLD]);

// Mapping from KeyTier to texture key
export const KEY_TEXTURE_MAP = Object.freeze({
  [KeyTier.METALLIC]: TEX_KEY_METALLIC,
  [KeyTier.COPPER]: TEX_KEY_COPPER,
  [KeyTier.GOLD]: TEX_KEY_GOLD,
});

// Chest Tiers (same names as key tiers for matching)
export const ChestTier = Object.freeze({
  METALLIC: 'metallic',
  COPPER: 'copper',
  GOLD: 'gold',
});

export const CHEST_TEXTURE_MAP = Object.freeze({
  [ChestTier.METALLIC]: TEX_CHEST_METALLIC,
  [ChestTier.COPPER]: TEX_CHEST_COPPER,
  [ChestTier.GOLD]: TEX_CHEST_GOLD,
});

// Chest Loot Types
export const ChestLoot = Object.freeze({
  EMPTY: 'empty',
  COIN_BAG: 'coin_bag',
  HEART: 'heart',
  STAR: 'star',
  AGMUTH: 'agmuth',
});

// Which chest tiers each key tier can open
// Metallic: only metallic. Copper: copper + metallic. Gold: all.
export const KEY_CAN_OPEN = Object.freeze({
  [KeyTier.METALLIC]: new Set([ChestTier.METALLIC]),
  [KeyTier.COPPER]: new Set([ChestTier.METALLIC, ChestTier.COPPER]),
  [KeyTier.GOLD]: new Set([ChestTier.METALLIC, ChestTier.COPPER, ChestTier.GOLD]),
});

// --- Food Types ---
export const FoodType = Object.freeze({
  FRUIT: 'fruit',
  CHICKEN: 'chicken',
  TUNA: 'tuna',
});

// Mapping from FoodType to texture key
export const FOOD_TEXTURE_MAP = Object.freeze({
  [FoodType.FRUIT]: TEX_FOOD_FRUIT,
  [FoodType.CHICKEN]: TEX_FOOD_CHICKEN,
  [FoodType.TUNA]: TEX_FOOD_TUNA,
});

// Mapping from FoodType to display name (shown when food is collected)
export const FOOD_NAME_MAP = Object.freeze({
  [FoodType.FRUIT]: 'Fruit',
  [FoodType.CHICKEN]: 'Chicken',
  [FoodType.TUNA]: 'Tuna',
});

// Array of all food types for random selection
export const FOOD_TYPES = Object.freeze([
  FoodType.FRUIT,
  FoodType.CHICKEN,
  FoodType.TUNA,
]);

// Mapping from FoodType to audio key (played on collection)
import {
  AUDIO_FOOD_FRUIT,
  AUDIO_FOOD_CHICKEN,
  AUDIO_FOOD_TUNA,
  FOOD_HEAL_FRUIT,
  FOOD_HEAL_CHICKEN,
  FOOD_HEAL_TUNA,
} from '../config/game-config.js';

export const FOOD_AUDIO_MAP = Object.freeze({
  [FoodType.FRUIT]: AUDIO_FOOD_FRUIT,
  [FoodType.CHICKEN]: AUDIO_FOOD_CHICKEN,
  [FoodType.TUNA]: AUDIO_FOOD_TUNA,
});

// Mapping from FoodType to heal amount (HP restored on collection)
export const FOOD_HEAL_MAP = Object.freeze({
  [FoodType.FRUIT]: FOOD_HEAL_FRUIT,
  [FoodType.CHICKEN]: FOOD_HEAL_CHICKEN,
  [FoodType.TUNA]: FOOD_HEAL_TUNA,
});

// --- Directions ---
export const Direction = Object.freeze({
  UP: 'up',
  DOWN: 'down',
  LEFT: 'left',
  RIGHT: 'right',
});

// --- Maze Cell Types ---
export const CellType = Object.freeze({
  WALL: 1,
  PATH: 0,
});

// --- Shop Item Types ---
import {
  SHOP_ITEM_THREIAS_GUIDE,
  SHOP_ITEM_HEART_POTION,
  SHOP_ITEM_TOTEM_VORACIOUS,
  SHOP_ITEM_AGMUTH_PENDANT,
  SHOP_ITEM_LAVA_RING,
  SHOP_ITEM_POISON_RING,
  SHOP_ITEM_WATER_RING,
  SHOP_ITEM_KEY_PENDANT,
  SHOP_ITEM_HEART_PENDANT,
  SHOP_ITEM_ELEMENTAL_RING,
  SHOP_ITEM_TEAR_OF_THREIA,
  SHOP_ITEM_HAMMER_OF_THREIA,
  SHOP_ITEM_KEY_METALLIC,
  SHOP_ITEM_KEY_COPPER,
  SHOP_ITEM_KEY_GOLD,
  SHOP_PRICE_THREIAS_GUIDE,
  SHOP_PRICE_HEART_POTION,
  SHOP_PRICE_TOTEM_VORACIOUS,
  SHOP_PRICE_AGMUTH_PENDANT,
  SHOP_PRICE_LAVA_RING,
  SHOP_PRICE_POISON_RING,
  SHOP_PRICE_WATER_RING,
  SHOP_PRICE_KEY_PENDANT,
  SHOP_PRICE_HEART_PENDANT,
  SHOP_PRICE_ELEMENTAL_RING,
  SHOP_PRICE_TEAR_OF_THREIA,
  SHOP_PRICE_HAMMER_OF_THREIA,
  SHOP_PRICE_KEY_METALLIC,
  SHOP_PRICE_KEY_COPPER,
  SHOP_PRICE_KEY_GOLD,
  SHOP_MAX_THREIAS_GUIDE,
  SHOP_MAX_HEART_POTION,
  SHOP_MAX_TOTEM_VORACIOUS,
  SHOP_MAX_AGMUTH_PENDANT,
  SHOP_MAX_LAVA_RING,
  SHOP_MAX_POISON_RING,
  SHOP_MAX_WATER_RING,
  SHOP_MAX_KEY_PENDANT,
  SHOP_MAX_HEART_PENDANT,
  SHOP_MAX_ELEMENTAL_RING,
  SHOP_MAX_TEAR_OF_THREIA,
  SHOP_MAX_HAMMER_OF_THREIA,
  SHOP_MAX_KEY_METALLIC,
  SHOP_MAX_KEY_COPPER,
  SHOP_MAX_KEY_GOLD,
  DURABILITY_THREIAS_GUIDE,
  DURABILITY_WATER_RING,
  DURABILITY_LAVA_RING,
  DURABILITY_POISON_RING,
  DURABILITY_ELEMENTAL_RING,
  DURABILITY_TOTEM_VORACIOUS,
  DURABILITY_HEART_PENDANT,
  DURABILITY_HAMMER_OF_THREIA,
} from '../config/game-config.js';

// --- Item Persistence Categories ---
// consumable: cleared on game over, can repurchase each run
// permanent: bought once, kept forever, never repurchasable
// durability: persists across game overs, degrades with use, repurchasable after breaking
export const ItemCategory = Object.freeze({
  CONSUMABLE: 'consumable',
  PERMANENT: 'permanent',
  DURABILITY: 'durability',
});

export const ShopItemType = Object.freeze({
  THREIAS_GUIDE: SHOP_ITEM_THREIAS_GUIDE,
  HEART_POTION: SHOP_ITEM_HEART_POTION,
  TOTEM_VORACIOUS: SHOP_ITEM_TOTEM_VORACIOUS,
  AGMUTH_PENDANT: SHOP_ITEM_AGMUTH_PENDANT,
  LAVA_RING: SHOP_ITEM_LAVA_RING,
  POISON_RING: SHOP_ITEM_POISON_RING,
  WATER_RING: SHOP_ITEM_WATER_RING,
  KEY_PENDANT: SHOP_ITEM_KEY_PENDANT,
  HEART_PENDANT: SHOP_ITEM_HEART_PENDANT,
  ELEMENTAL_RING: SHOP_ITEM_ELEMENTAL_RING,
  TEAR_OF_THREIA: SHOP_ITEM_TEAR_OF_THREIA,
  HAMMER_OF_THREIA: SHOP_ITEM_HAMMER_OF_THREIA,
  KEY_METALLIC: SHOP_ITEM_KEY_METALLIC,
  KEY_COPPER: SHOP_ITEM_KEY_COPPER,
  KEY_GOLD: SHOP_ITEM_KEY_GOLD,
});

// Shop item category classification
export const SHOP_ITEM_CATEGORIES = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: ItemCategory.DURABILITY,
  [ShopItemType.HEART_POTION]: ItemCategory.CONSUMABLE,
  [ShopItemType.TOTEM_VORACIOUS]: ItemCategory.DURABILITY,
  [ShopItemType.AGMUTH_PENDANT]: ItemCategory.PERMANENT,
  [ShopItemType.LAVA_RING]: ItemCategory.DURABILITY,
  [ShopItemType.POISON_RING]: ItemCategory.DURABILITY,
  [ShopItemType.WATER_RING]: ItemCategory.DURABILITY,
  [ShopItemType.KEY_PENDANT]: ItemCategory.PERMANENT,
  [ShopItemType.HEART_PENDANT]: ItemCategory.DURABILITY,
  [ShopItemType.ELEMENTAL_RING]: ItemCategory.DURABILITY,
  [ShopItemType.TEAR_OF_THREIA]: ItemCategory.CONSUMABLE,
  [ShopItemType.HAMMER_OF_THREIA]: ItemCategory.DURABILITY,
  [ShopItemType.KEY_METALLIC]: ItemCategory.CONSUMABLE,
  [ShopItemType.KEY_COPPER]: ItemCategory.CONSUMABLE,
  [ShopItemType.KEY_GOLD]: ItemCategory.CONSUMABLE,
});

// Max durability charges for durability-category items
export const SHOP_ITEM_DURABILITY = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: DURABILITY_THREIAS_GUIDE,
  [ShopItemType.WATER_RING]: DURABILITY_WATER_RING,
  [ShopItemType.LAVA_RING]: DURABILITY_LAVA_RING,
  [ShopItemType.POISON_RING]: DURABILITY_POISON_RING,
  [ShopItemType.ELEMENTAL_RING]: DURABILITY_ELEMENTAL_RING,
  [ShopItemType.TOTEM_VORACIOUS]: DURABILITY_TOTEM_VORACIOUS,
  [ShopItemType.HEART_PENDANT]: DURABILITY_HEART_PENDANT,
  [ShopItemType.HAMMER_OF_THREIA]: DURABILITY_HAMMER_OF_THREIA,
});

// Shop item display names
export const SHOP_ITEM_NAMES = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: "Threia's Guide",
  [ShopItemType.HEART_POTION]: 'Heart Potion',
  [ShopItemType.TOTEM_VORACIOUS]: 'Totem of the Voracious',
  [ShopItemType.AGMUTH_PENDANT]: 'Agmuth Pendant',
  [ShopItemType.LAVA_RING]: 'Lava Ring',
  [ShopItemType.POISON_RING]: 'Poison Ring',
  [ShopItemType.WATER_RING]: 'Water Ring',
  [ShopItemType.KEY_PENDANT]: 'Key Pendant',
  [ShopItemType.HEART_PENDANT]: 'Heart Pendant',
  [ShopItemType.ELEMENTAL_RING]: 'Elemental Ring',
  [ShopItemType.TEAR_OF_THREIA]: 'Tear of Threia',
  [ShopItemType.HAMMER_OF_THREIA]: 'Hammer of Threia',
  [ShopItemType.KEY_METALLIC]: 'Metallic Key',
  [ShopItemType.KEY_COPPER]: 'Copper Key',
  [ShopItemType.KEY_GOLD]: 'Gold Key',
});

// Shop item descriptions (short, for shop UI)
export const SHOP_ITEM_DESCRIPTIONS = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: `40% chance per level to reveal the entire maze (${DURABILITY_THREIAS_GUIDE} charges)`,
  [ShopItemType.HEART_POTION]: 'Auto-restores a lost heart with full energy',
  [ShopItemType.TOTEM_VORACIOUS]: `Food within 5 tiles moves toward you (${DURABILITY_TOTEM_VORACIOUS} charges)`,
  [ShopItemType.AGMUTH_PENDANT]: '+30% Agmuth drops from copper/gold chests (permanent)',
  [ShopItemType.LAVA_RING]: `Immune to lava floor damage (${DURABILITY_LAVA_RING} charges)`,
  [ShopItemType.POISON_RING]: `Immune to poison floor effects (${DURABILITY_POISON_RING} charges)`,
  [ShopItemType.WATER_RING]: `Immune to water floor damage (${DURABILITY_WATER_RING} charges)`,
  [ShopItemType.KEY_PENDANT]: '+20% key spawn probability (permanent)',
  [ShopItemType.HEART_PENDANT]: `20% less energy drain per step (${DURABILITY_HEART_PENDANT} charges)`,
  [ShopItemType.ELEMENTAL_RING]: `Immune to lava, water, and poison floors (${DURABILITY_ELEMENTAL_RING} charges)`,
  [ShopItemType.TEAR_OF_THREIA]: 'Prevents game over — restores all hearts and full energy',
  [ShopItemType.HAMMER_OF_THREIA]: `Break walls with the power of Threia (${DURABILITY_HAMMER_OF_THREIA} charges)`,
  [ShopItemType.KEY_METALLIC]: 'Opens metallic chests — delivered at game start',
  [ShopItemType.KEY_COPPER]: 'Opens metallic and copper chests — delivered at game start',
  [ShopItemType.KEY_GOLD]: 'Opens any chest — delivered at game start',
});

// Shop item prices
export const SHOP_ITEM_PRICES = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: SHOP_PRICE_THREIAS_GUIDE,
  [ShopItemType.HEART_POTION]: SHOP_PRICE_HEART_POTION,
  [ShopItemType.TOTEM_VORACIOUS]: SHOP_PRICE_TOTEM_VORACIOUS,
  [ShopItemType.AGMUTH_PENDANT]: SHOP_PRICE_AGMUTH_PENDANT,
  [ShopItemType.LAVA_RING]: SHOP_PRICE_LAVA_RING,
  [ShopItemType.POISON_RING]: SHOP_PRICE_POISON_RING,
  [ShopItemType.WATER_RING]: SHOP_PRICE_WATER_RING,
  [ShopItemType.KEY_PENDANT]: SHOP_PRICE_KEY_PENDANT,
  [ShopItemType.HEART_PENDANT]: SHOP_PRICE_HEART_PENDANT,
  [ShopItemType.ELEMENTAL_RING]: SHOP_PRICE_ELEMENTAL_RING,
  [ShopItemType.TEAR_OF_THREIA]: SHOP_PRICE_TEAR_OF_THREIA,
  [ShopItemType.HAMMER_OF_THREIA]: SHOP_PRICE_HAMMER_OF_THREIA,
  [ShopItemType.KEY_METALLIC]: SHOP_PRICE_KEY_METALLIC,
  [ShopItemType.KEY_COPPER]: SHOP_PRICE_KEY_COPPER,
  [ShopItemType.KEY_GOLD]: SHOP_PRICE_KEY_GOLD,
});

// Shop item max quantities
export const SHOP_ITEM_MAX = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: SHOP_MAX_THREIAS_GUIDE,
  [ShopItemType.HEART_POTION]: SHOP_MAX_HEART_POTION,
  [ShopItemType.TOTEM_VORACIOUS]: SHOP_MAX_TOTEM_VORACIOUS,
  [ShopItemType.AGMUTH_PENDANT]: SHOP_MAX_AGMUTH_PENDANT,
  [ShopItemType.LAVA_RING]: SHOP_MAX_LAVA_RING,
  [ShopItemType.POISON_RING]: SHOP_MAX_POISON_RING,
  [ShopItemType.WATER_RING]: SHOP_MAX_WATER_RING,
  [ShopItemType.KEY_PENDANT]: SHOP_MAX_KEY_PENDANT,
  [ShopItemType.HEART_PENDANT]: SHOP_MAX_HEART_PENDANT,
  [ShopItemType.ELEMENTAL_RING]: SHOP_MAX_ELEMENTAL_RING,
  [ShopItemType.TEAR_OF_THREIA]: SHOP_MAX_TEAR_OF_THREIA,
  [ShopItemType.HAMMER_OF_THREIA]: SHOP_MAX_HAMMER_OF_THREIA,
  [ShopItemType.KEY_METALLIC]: SHOP_MAX_KEY_METALLIC,
  [ShopItemType.KEY_COPPER]: SHOP_MAX_KEY_COPPER,
  [ShopItemType.KEY_GOLD]: SHOP_MAX_KEY_GOLD,
});

// Shop item texture mapping
export const SHOP_ITEM_TEXTURES = Object.freeze({
  [ShopItemType.THREIAS_GUIDE]: TEX_SHOP_THREIAS_GUIDE,
  [ShopItemType.HEART_POTION]: TEX_SHOP_HEART_POTION,
  [ShopItemType.TOTEM_VORACIOUS]: TEX_SHOP_TOTEM_VORACIOUS,
  [ShopItemType.AGMUTH_PENDANT]: TEX_SHOP_AGMUTH_PENDANT,
  [ShopItemType.LAVA_RING]: TEX_SHOP_LAVA_RING,
  [ShopItemType.POISON_RING]: TEX_SHOP_POISON_RING,
  [ShopItemType.WATER_RING]: TEX_SHOP_WATER_RING,
  [ShopItemType.KEY_PENDANT]: TEX_SHOP_KEY_PENDANT,
  [ShopItemType.HEART_PENDANT]: TEX_SHOP_HEART_PENDANT,
  [ShopItemType.ELEMENTAL_RING]: TEX_SHOP_ELEMENTAL_RING,
  [ShopItemType.TEAR_OF_THREIA]: TEX_SHOP_TEAR_OF_THREIA,
  [ShopItemType.HAMMER_OF_THREIA]: TEX_SHOP_HAMMER_OF_THREIA,
  [ShopItemType.KEY_METALLIC]: TEX_KEY_METALLIC,
  [ShopItemType.KEY_COPPER]: TEX_KEY_COPPER,
  [ShopItemType.KEY_GOLD]: TEX_KEY_GOLD,
});

// --- Game Events (emitted via Phaser EventEmitter) ---
export const Events = Object.freeze({
  HEALTH_CHANGED: 'health-changed',
  LIFE_LOST: 'life-lost',
  LIFE_GAINED: 'life-gained',
  GAME_OVER: 'game-over',
  COINS_CHANGED: 'coins-changed',
  SCORE_CHANGED: 'score-changed',
  LEVEL_COMPLETE: 'level-complete',
  LEVEL_CHANGED: 'level-changed',
  PLAYER_MOVED: 'player-moved',
  MUSIC_TOGGLED: 'music-toggled',
  FOOD_COLLECTED: 'food-collected',
  COIN_COLLECTED: 'coin-collected',
  PLAYER_RESTING: 'player-resting',
  PLAYER_REST_STOPPED: 'player-rest-stopped',
  PLAYER_POISONED: 'player-poisoned',
  PLAYER_POISON_ENDED: 'player-poison-ended',
  PLAYER_WOUNDED: 'player-wounded',
  PLAYER_WOUND_ENDED: 'player-wound-ended',
  KEY_COLLECTED: 'key-collected',
  KEY_USED: 'key-used',
  CHEST_OPENED: 'chest-opened',
  AGMUTH_COLLECTED: 'agmuth-collected',
  KEYS_CHANGED: 'keys-changed',
  AGMUTH_CHANGED: 'agmuth-changed',
  AGMUTH_ACTIVATED: 'agmuth-activated',
  AGMUTH_DEACTIVATED: 'agmuth-deactivated',
  COMPASS_COLLECTED: 'compass-collected',
  COMPASS_DISINTEGRATE: 'compass-disintegrate',
  THREIA_TOTEM_COLLECTED: 'threia-totem-collected',
  THREIA_BLESSING_START: 'threia-blessing-start',
  THREIA_BLESSING_SUCCESS: 'threia-blessing-success',
  THREIA_BLESSING_FAIL: 'threia-blessing-fail',
  THREIA_TOTEM_CONSUMED: 'threia-totem-consumed',
  THREIA_PRAYER_PHASE: 'threia-prayer-phase',
  // Shop system events
  SHOP_ITEMS_CHANGED: 'shop-items-changed',
  HEART_POTION_USED: 'heart-potion-used',
  HEART_POTION_CHANGED: 'heart-potion-changed',
  THREIAS_GUIDE_REVEAL: 'threias-guide-reveal',
  FOOD_ATTRACTED: 'food-attracted',
  RING_ACTIVATED: 'ring-activated',
  RING_DEACTIVATED: 'ring-deactivated',
  // Durability events
  ITEM_DURABILITY_CHANGED: 'item-durability-changed',
  ITEM_BROKEN: 'item-broken',
  // Bat events
  BAT_SPAWNED: 'bat-spawned',
  BAT_ATTACK: 'bat-attack',
  BAT_HIT: 'bat-hit',
  BAT_REPELLED: 'bat-repelled',
  // Manuscript events
  MANUSCRIPT_COLLECTED: 'manuscript-collected',
  // Tear of Threia events
  TEAR_OF_THREIA_USED: 'tear-of-threia-used',
  // Hammer of Threia events
  HAMMER_COLLECTED: 'hammer-collected',
  HAMMER_HIT: 'hammer-hit',
  HAMMER_WALL_BROKEN: 'hammer-wall-broken',
  HAMMER_CHARGES_CHANGED: 'hammer-charges-changed',
  WALL_REGENERATED: 'wall-regenerated',
  // Narrator events
  NARRATOR_MONK_SPEAK: 'narrator-monk-speak',
  NARRATOR_VORATH_SPEAK: 'narrator-vorath-speak',
  NARRATOR_AGMUTH_SPEAK: 'narrator-agmuth-speak',
  NARRATOR_DISMISSED: 'narrator-dismissed',
  NARRATOR_GIFT_RECEIVED: 'narrator-gift-received',
  // Treasure room events
  TREASURE_ROOM_ENTER: 'treasure-room-enter',
  TREASURE_ROOM_EXIT: 'treasure-room-exit',
  TREASURE_ROOM_TIMER_UPDATE: 'treasure-room-timer-update',
  TREASURE_ROOM_WARNING: 'treasure-room-warning',
  // Final battle events
  FINAL_BATTLE_START: 'final-battle-start',
  FINAL_ALTAR_ACTIVATED: 'final-altar-activated',
  FINAL_VORATH_DESTROYED: 'final-vorath-destroyed',
  FINAL_GAME_COMPLETE: 'final-game-complete',
  // Level event events
  LEVEL_EVENT_ACTIVATED: 'level-event-activated',
  LEVEL_EVENT_DEACTIVATED: 'level-event-deactivated',
  // Achievement events
  ACHIEVEMENT_UNLOCKED: 'achievement-unlocked',
  // Moving wall events
  MOVING_WALL_WARNING: 'moving-wall-warning',
  MOVING_WALL_SHIFTED: 'moving-wall-shifted',
  // Secret passage events
  SECRET_PASSAGE_DISCOVERED: 'secret-passage-discovered',
  // Shadow walker events
  SHADOW_WALKER_SPAWNED: 'shadow-walker-spawned',
  SHADOW_WALKER_SPOTTED_PLAYER: 'shadow-walker-spotted-player',
  SHADOW_WALKER_HIT: 'shadow-walker-hit',
  SHADOW_WALKER_DESPAWNED: 'shadow-walker-despawned',
  // Bat nest events
  BAT_NEST_ACTIVATED: 'bat-nest-activated',
  BAT_NEST_DESTROYED: 'bat-nest-destroyed',
  BAT_NEST_TRAVERSED: 'bat-nest-traversed',
  // Mini-map events
  MINIMAP_TOGGLED: 'minimap-toggled',
});
